
import { useState } from "react"
import { useConfirmImport } from "../../hooks/useProjectImport"
import type { ProjectImportRow } from "../../hooks/types"

interface ConfirmStepProps {
  parsedRecords: ProjectImportRow[]
  onBack: () => void
  onSuccess: () => void
  onError: (message: string) => void
}

export default function ConfirmStep({ parsedRecords, onBack, onSuccess, onError }: ConfirmStepProps) {
  const [result, setResult] = useState<{ created: number; updated: number } | null>(null)
  const confirmImport = useConfirmImport()

  const handleCommit = async () => {
    try {
      // Separate records with and without conflicts
      const newRows = parsedRecords.filter((r) => !r.conflictType.length)
      const upsertRows = parsedRecords.filter(
        (r) => r.conflictType.includes("duplicateCode") && !r.conflictType.includes("missingClientEmail"),
      )

      const result = await confirmImport.mutateAsync({ newRows, upsertRows })
      setResult(result)
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to import projects"
      onError(message)
    }
  }

  if (result) {
    return (
      <div className="text-center py-8">
        {result.created > 0 || result.updated > 0 ? (
          <>
            <svg
              className="h-16 w-16 text-green-500 mx-auto mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-xl font-medium mb-2">Import Successful</h3>
            <p className="text-gray-500 mb-6">
              {result.created} projects created and {result.updated} projects updated.
            </p>
          </>
        ) : (
          <>
            <svg className="h-16 w-16 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-xl font-medium mb-2">No Changes Made</h3>
            <p className="text-gray-500 mb-6">No projects were created or updated.</p>
          </>
        )}
        <button
          type="button"
          onClick={onSuccess}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Done
        </button>
      </div>
    )
  }

  // Count records by type
  const validRecords = parsedRecords.filter((r) => !r.conflictType.length).length
  const duplicateRecords = parsedRecords.filter(
    (r) => r.conflictType.includes("duplicateCode") && !r.conflictType.includes("missingClientEmail"),
  ).length
  const invalidRecords = parsedRecords.filter(
    (r) => r.conflictType.includes("missingClientEmail") || r.conflictType.includes("missingClientContact"),
  ).length

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Confirm Import</h3>
        <p className="text-gray-500 mb-4">Review the summary below and confirm to import projects.</p>
      </div>

      <div className="space-y-4">
        <div className="bg-white p-4 rounded-lg border">
          <h4 className="font-medium mb-2">Import Summary</h4>
          <ul className="space-y-2">
            <li className="flex justify-between">
              <span>New projects to create:</span>
              <span className="font-medium">{validRecords}</span>
            </li>
            <li className="flex justify-between">
              <span>Existing projects to update:</span>
              <span className="font-medium">{duplicateRecords}</span>
            </li>
            <li className="flex justify-between text-red-600">
              <span>Records with errors (will be skipped):</span>
              <span className="font-medium">{invalidRecords}</span>
            </li>
            <li className="flex justify-between font-medium pt-2 border-t">
              <span>Total records:</span>
              <span>{parsedRecords.length}</span>
            </li>
          </ul>
        </div>

        {invalidRecords > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg text-yellow-800">
            <p className="font-medium">Warning</p>
            <p className="text-sm">
              {invalidRecords} records have errors and will be skipped during import. Common issues include missing
              client emails or clients that don't exist in your system.
            </p>
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <button type="button" onClick={onBack} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
          Back
        </button>
        <button
          type="button"
          onClick={handleCommit}
          disabled={confirmImport.isPending || (validRecords === 0 && duplicateRecords === 0)}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
        >
          {confirmImport.isPending ? (
            <span className="flex items-center">
              <svg
                className="mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Importing...
            </span>
          ) : (
            "Confirm Import"
          )}
        </button>
      </div>
    </div>
  )
}
